<script setup lang="ts">
import { onBeforeMount, PropType, ref, Ref, toRefs, watch } from "vue";
import { useApiClient } from "@/api/client";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import { useRoute } from "vue-router";
import {
	required,
	minLength,
	maxLength,
	email,
} from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import { loadingWrapperAll } from "@/api/helpers/loadingWrapper";

const { t } = useI18n();

const props = defineProps({
	availableNotificationEvents: {
		type: Object as PropType<App.Data.NotificationEventDO[]>,
		require: true,
	},
	configuredNotificationEvents: {
		type: Object as PropType<App.Data.NotificationEventDO[]>,
		required: true,
	},
	asyncUpdateFunction: {
		type: Function,
		required: true,
	},
});

const configuredNotificationEvents = ref(props.configuredNotificationEvents);

watch(
	configuredNotificationEvents,
	async (nv) => {
		await toastApiResult(props.asyncUpdateFunction(nv));
	},
	{ deep: true }
);

// Emit is not used in this component but required through the tabs-router-view setup.
const emit = defineEmits(["update-user"]);
</script>

<template>
	<module-loading>
		<form>
			<v-row class="pt-0 mt-0">
				<v-col cols="12">
					<div v-for="event in availableNotificationEvents" :key="event.uuid">
						<div class="d-flex align-sm-center align-start">
							<v-avatar
								size="48"
								class="mr-2"
								rounded="md"
								color="lightprimary"
							>
								<v-icon color="primary"> fa:fa-duotone fa-bell-on</v-icon>
							</v-avatar>
							<div class="pl-4">
								<h6 class="text-h6 mb-1 mt-1">
									{{ event.name }}
								</h6>
								<h5 class="text-subtitle-1 text-medium-emphasis">
									{{ event.description }}
								</h5>
							</div>
							<div
								class="ml-auto mr-1 pa-sm-1 pa-6 pl-7"
								v-if="
									event.name_clean === 'user_defined_notification' ||
									event.name_clean === 'motives_information_notification'
								"
							>
								<v-checkbox
									disabled
									v-tooltip="`You cant unsubscribe from this Event.`"
									v-model="configuredNotificationEvents"
									:value="event"
									color="primary"
									hide-details
								/>
							</div>
							<div class="ml-auto mr-1 pa-sm-1 pa-6 pl-7" v-else>
								<v-checkbox
									v-model="configuredNotificationEvents"
									:value="event"
									color="primary"
									return-object
									hide-details
									inset
								></v-checkbox>
							</div>
						</div>
					</div>
				</v-col>
			</v-row>
		</form>
	</module-loading>
</template>

<style lang="scss">
.v-expansion-panel-title__overlay {
	background: rgba(var(--v-theme-primary)) !important;
}
</style>
